import React, { ForwardRefRenderFunction, forwardRef } from "react";

//#region types
import * as Yup from "yup";
import { FormHandles } from "models/propTypes/genericFormProps";
import { DocumentFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { CommonEnums, StyleEnums } from "models/enums";
import { ConvertToGenericDocumentFormContainerProps } from "models/propTypes/receiptPropTypes";
//#endregion

//#region components
import { useDocumentDraftService } from "serviceHooks";
import ConvertToGenericDocumentForm from "./convertToGenericDocumentForm";
import { FloatingMessageWithLink, useFloatingMessages } from "components/common";
//#endregion
import { TranslationKeys } from "translations";
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";

const ConvertToGenericDocumentFormContainer: ForwardRefRenderFunction<
  FormHandles,
  ConvertToGenericDocumentFormContainerProps
> = (props, ref) => {
  const { classifiedReceipt } = props;
  const documentDraftService = useDocumentDraftService();

  const validationSchema = Yup.object<DocumentFormTypes.SendBack>({
    message: Yup.string().required(TranslationKeys.Validations.Required),
  });

  const { enqueueMessage } = useFloatingMessages();

  const defaultValues: DocumentFormTypes.SendBack = {
    message: "",
  };

  const buildFiles = (images: WintSuperkollAPIModel.ClassifiedReceiptDraftImage[] = []) =>
    images.map((attachment) => ({
      fileName: attachment.fileName ?? "",
      contentType: attachment.contentType ?? "",
      data: attachment.data ?? "",
    }));

  const buildCreateNewPayload = (
    files: ReturnType<typeof buildFiles>
  ): DocumentFormTypes.CreateNew => ({
    filesToUpload: files,
    document: {
      bookingDate: classifiedReceipt.created,
    },
    type: CommonEnums.DocumentTypes.GenericDocument,
    receipt: {} as DocumentFormTypes.CreateReceipt,
    incomingInvoice: {} as DocumentFormTypes.CreateIncomingInvoice,
  });

  const handleSubmit = async (values: DocumentFormTypes.SendBack) => {

    const documentDraftPost: WintSuperkollAPIModel.DocumentDraftPost = {
      id: classifiedReceipt.id,
      type: WintSuperkollAPIModel.DocumentDraftType.GenericDocument,
      message: values.message,
    };

    const files = buildFiles(classifiedReceipt.images);
    const createNewPayload = buildCreateNewPayload(files);

    documentDraftPost.genericDocument = documentDraftService.formToGenericDocumentPost(createNewPayload);

    await documentDraftService.post(documentDraftPost);

    enqueueMessage(
      <FloatingMessageWithLink
        message={LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Receipt.ClassifiedReceiptMoreActionsConvertToGenericDocumentSuccessMessage
        )}
        link={UrlHelper.formatPath(
          `/Reports/AccountingDocuments?states=${WintSuperkollAPIModel.GenericDocumentState.SentIn}`
        )}
      />,
      StyleEnums.DisplayType.Success,
      true,
      true,
      { autoHideDuration: 8000 }
    );
  };

  return (
    <ConvertToGenericDocumentForm
      ref={ref}
      validationSchema={validationSchema}
      defaultValues={defaultValues}
      handleSubmit={handleSubmit}
    />
  );
};

export default forwardRef(ConvertToGenericDocumentFormContainer);
